import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Typography } from '@mui/material';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import type { RouteAlternative, RoutingStrategy } from '../../services/waveService';

interface RouteAlternativesBarProps {
  alternatives: RouteAlternative[];
  selectedStrategy: RoutingStrategy;
  onSelect: (strategy: RoutingStrategy) => void;
}

function formatDistance(meters: number): string {
  if (!Number.isFinite(meters) || meters <= 0) return '—';
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`;
  return `${Math.round(meters)} m`;
}

/**
 * Google Maps tarzı alternatif rota chip şeridi.
 * Tek aday kaldıysa (aynı durak sırası) gizlenir.
 */
const RouteAlternativesBar: React.FC<RouteAlternativesBarProps> = ({
  alternatives,
  selectedStrategy,
  onSelect,
}) => {
  const { t } = useTranslation('warehouse');

  if (!alternatives || alternatives.length < 2) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        flexWrap: 'wrap',
        px: 0.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.25 }}>
        <AltRouteIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
          {t('screens.picking.routeAlternatives.title', { defaultValue: 'Rotalar' })}
        </Typography>
      </Box>
      {alternatives.map((alt) => {
        const selected = alt.strategy === selectedStrategy || alt.isSelected;
        const label = alt.label
          || t(`screens.picking.routeAlternatives.${alt.strategy}`, {
            defaultValue: alt.strategy === 'leftFirst'
              ? 'Soldan başla'
              : alt.strategy === 'rightFirst'
                ? 'Sağdan başla'
                : 'En kısa',
          });
        return (
          <Chip
            key={alt.strategy}
            size="small"
            color={selected ? 'primary' : 'default'}
            variant={selected ? 'filled' : 'outlined'}
            onClick={() => {
              if (!selected) onSelect(alt.strategy);
            }}
            label={`${label} · ${formatDistance(alt.estimatedDistance)}`}
            sx={{
              fontWeight: selected ? 700 : 500,
              height: 28,
              cursor: selected ? 'default' : 'pointer',
            }}
          />
        );
      })}
    </Box>
  );
};

export default RouteAlternativesBar;
